import { useState, useEffect } from "react";
import styles from "./CreateHomeSection.module.css";
import placeholderImage from "../../assets/images/image-upload-placeholder.png";
import { ENDPOINTS } from "../../utils/endpoints";
import api from "../../services/api";
import { toast } from "react-toastify";
import useDashboardStore from "../../store/dashboardStore";

const CreateHomeSection = () => {
  const { setIsAllHomeSectionScreen, editHomeSectionId } = useDashboardStore();

  const [formData, setFormData] = useState({
    title: "",
    title_background_color: "",
    title_text_color_light: "#000000",
    title_text_color_dark: "#FFFFFF",
    title_alignment: "center",
    body_text: "",
    body_background_color: "",
    body_text_color_light: "#000000",
    body_text_color_dark: "#FFFFFF",
    body_text_alignment: "left",
    button_text: "",
    button_link: "",
    button_alignment: "left",
    is_active: true,
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (editHomeSectionId !== null) {
      fetchHomeSectionData();
    } else {
      resetForm();
    }
  }, [editHomeSectionId]);

  const fetchHomeSectionData = async () => {
    try {
      const { data } = await api.get(
        ENDPOINTS.GET_HOME_SECTION_BY_ID(editHomeSectionId)
      );
      if (data.success) {
        const section = data.section;
        setFormData({
          title: section.title || "",
          title_background_color: section.title_background_color || "",
          title_text_color_light: section.title_text_color_light || "#000000",
          title_text_color_dark: section.title_text_color_dark || "#FFFFFF",
          title_alignment: section.title_alignment || "center",
          body_text: section.body_text || "",
          body_background_color: section.body_background_color || "",
          body_text_color_light: section.body_text_color_light || "#000000",
          body_text_color_dark: section.body_text_color_dark || "#FFFFFF",
          body_text_alignment: section.body_text_alignment || "left",
          button_text: section.button_text || "",
          button_link: section.button_link || "",
          button_alignment: section.button_alignment || "left",
          is_active: section.is_active === 1,
        });

        if (section.image_url) {
          setImagePreview(`${ENDPOINTS.IMAGE_BASE_URL}${section.image_url}`);
        }
      }
    } catch (err) {
      console.error("Failed to fetch home section", err);
      toast.error("Failed to fetch home section data");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleColorChange = (field, color) => {
    setFormData((prev) => ({ ...prev, [field]: color }));
  };

  const clearColor = (field) => {
    setFormData((prev) => ({ ...prev, [field]: "" }));
  };

  const handleSubmit = async () => {
    if (isLoading) return;

    // Validation - at least one content field required
    if (
      !imageFile &&
      !imagePreview &&
      !formData.title.trim() &&
      !formData.body_text.trim()
    ) {
      toast.error("Please provide at least one: Image, Title, or Body Text");
      return;
    }

    setIsLoading(true);

    try {
      const formDataToSend = new FormData();

      // Append all form fields
      Object.keys(formData).forEach((key) => {
        if (key === "is_active") {
          formDataToSend.append(key, formData[key] ? "true" : "false");
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Append image if selected
      if (imageFile) {
        formDataToSend.append("image", imageFile);
      }

      let response;
      if (editHomeSectionId !== null) {
        response = await api.put(
          ENDPOINTS.UPDATE_HOME_SECTION_BY_ID(editHomeSectionId),
          formDataToSend,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
      } else {
        response = await api.post(
          ENDPOINTS.CREATE_HOME_SECTION,
          formDataToSend,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
      }

      if (response.data.success) {
        toast.success(
          response.data.message ||
            `Home section ${
              editHomeSectionId !== null ? "updated" : "created"
            } successfully`
        );
        setIsAllHomeSectionScreen();
      }
    } catch (err) {
      console.error("Failed to save home section", err);
      toast.error("Failed to save home section");
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      title_background_color: "",
      title_text_color_light: "#000000",
      title_text_color_dark: "#FFFFFF",
      title_alignment: "center",
      body_text: "",
      body_background_color: "",
      body_text_color_light: "#000000",
      body_text_color_dark: "#FFFFFF",
      body_text_alignment: "left",
      button_text: "",
      button_link: "",
      button_alignment: "left",
      is_active: true,
    });
    setImageFile(null);
    setImagePreview(null);
  };

  return (
    <div className={styles.pageWrapper}>
      {/* App Bar */}
      <div className={styles.appbarContainer}>
        <h2 className={styles.appbarTitle}>
          {editHomeSectionId === null ? "Create" : "Update"} Home Section
        </h2>
      </div>

      <div className={styles.bodyContainer}>
        {/* Left Section */}
        <div className={styles.leftSection}>
          {/* Image Upload */}
          <div className={styles.imageSectionWrapper}>
            <div className={styles.featuredImageBox}>
              {imagePreview && (
                <img
                  src={imagePreview}
                  alt="Preview"
                  className={styles.featuredImage}
                />
              )}
              {!imagePreview && (
                <img
                  src={placeholderImage}
                  alt="Placeholder"
                  className={styles.placeholderIcon}
                />
              )}
              <div className={styles.uploadLinkWrapper}>
                <label className={styles.uploadLink}>
                  <span className={styles.plusIcon}>+</span> Choose Image
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleImageChange}
                    disabled={isLoading}
                  />
                </label>
              </div>
            </div>
          </div>

          {/* Title */}
          <div className={styles.rowInputGroup}>
            <label className={styles.inlineLabel}>Section Title</label>
            <textarea
              name="title"
              value={formData.title}
              onChange={handleChange}
              className={styles.textArea}
              rows="2"
              placeholder="Enter section title..."
              disabled={isLoading}
            />
          </div>

          {/* Title Background Color */}
          <div className={styles.rowInputGroup}>
            <label className={styles.inlineLabel}>Title BG Color</label>
            <div className={styles.colorPickerWrapper}>
              <input
                type="color"
                value={formData.title_background_color || "#000000"}
                onChange={(e) =>
                  handleColorChange("title_background_color", e.target.value)
                }
                className={styles.colorInput}
                disabled={isLoading}
              />
              {formData.title_background_color && (
                <button
                  type="button"
                  onClick={() => clearColor("title_background_color")}
                  className={styles.clearButton}
                  disabled={isLoading}
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Title Text Colors */}
          <div className={styles.rowInputGroup}>
            <label className={styles.inlineLabel}>Title Text (Light)</label>
            <div className={styles.colorPickerWrapper}>
              <input
                type="color"
                value={formData.title_text_color_light}
                onChange={(e) =>
                  handleColorChange("title_text_color_light", e.target.value)
                }
                className={styles.colorInput}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className={styles.rowInputGroup}>
            <label className={styles.inlineLabel}>Title Text (Dark)</label>
            <div className={styles.colorPickerWrapper}>
              <input
                type="color"
                value={formData.title_text_color_dark}
                onChange={(e) =>
                  handleColorChange("title_text_color_dark", e.target.value)
                }
                className={styles.colorInput}
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Title Alignment */}
          <div className={styles.rowInputGroup}>
            <label className={styles.inlineLabel}>Title Alignment</label>
            <div className={styles.toggleWrapper}>
              <button
                type="button"
                className={`${styles.toggleOption} ${
                  formData.title_alignment === "left" ? styles.activeToggle : ""
                }`}
                onClick={() =>
                  setFormData((prev) => ({ ...prev, title_alignment: "left" }))
                }
                disabled={isLoading}
              >
                Left
              </button>
              <button
                type="button"
                className={`${styles.toggleOption} ${
                  formData.title_alignment === "center"
                    ? styles.activeToggle
                    : ""
                }`}
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    title_alignment: "center",
                  }))
                }
                disabled={isLoading}
              >
                Center
              </button>
              <button
                type="button"
                className={`${styles.toggleOption} ${
                  formData.title_alignment === "right"
                    ? styles.activeToggle
                    : ""
                }`}
                onClick={() =>
                  setFormData((prev) => ({ ...prev, title_alignment: "right" }))
                }
                disabled={isLoading}
              >
                Right
              </button>
            </div>
          </div>
        </div>

        {/* Right Section */}
        <div className={styles.rightSection}>
          {/* Body Text */}
          <div className={styles.rowInputGroup}>
            <label className={styles.inlineLabel}>Body Text</label>
            <textarea
              name="body_text"
              value={formData.body_text}
              onChange={handleChange}
              className={styles.textAreaLarge}
              rows="4"
              placeholder="Enter body text... Use **text** for bold"
              disabled={isLoading}
            />
          </div>
          <span className={styles.hint}>
            Tip: Use **text** to make text bold
          </span>

          {/* Body Background Color */}
          <div className={styles.rowInputGroup}>
            <label className={styles.inlineLabel}>Body BG Color</label>
            <div className={styles.colorPickerWrapper}>
              <input
                type="color"
                value={formData.body_background_color || "#000000"}
                onChange={(e) =>
                  handleColorChange("body_background_color", e.target.value)
                }
                className={styles.colorInput}
                disabled={isLoading}
              />
              {formData.body_background_color && (
                <button
                  type="button"
                  onClick={() => clearColor("body_background_color")}
                  className={styles.clearButton}
                  disabled={isLoading}
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Body Text Colors */}
          <div className={styles.rowInputGroup}>
            <label className={styles.inlineLabel}>Body Text (Light)</label>
            <div className={styles.colorPickerWrapper}>
              <input
                type="color"
                value={formData.body_text_color_light}
                onChange={(e) =>
                  handleColorChange("body_text_color_light", e.target.value)
                }
                className={styles.colorInput}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className={styles.rowInputGroup}>
            <label className={styles.inlineLabel}>Body Text (Dark)</label>
            <div className={styles.colorPickerWrapper}>
              <input
                type="color"
                value={formData.body_text_color_dark}
                onChange={(e) =>
                  handleColorChange("body_text_color_dark", e.target.value)
                }
                className={styles.colorInput}
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Body Alignment */}
          <div className={styles.rowInputGroup}>
            <label className={styles.inlineLabel}>Body Alignment</label>
            <div className={styles.toggleWrapper}>
              <button
                type="button"
                className={`${styles.toggleOption} ${
                  formData.body_text_alignment === "left"
                    ? styles.activeToggle
                    : ""
                }`}
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    body_text_alignment: "left",
                  }))
                }
                disabled={isLoading}
              >
                Left
              </button>
              <button
                type="button"
                className={`${styles.toggleOption} ${
                  formData.body_text_alignment === "center"
                    ? styles.activeToggle
                    : ""
                }`}
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    body_text_alignment: "center",
                  }))
                }
                disabled={isLoading}
              >
                Center
              </button>
              <button
                type="button"
                className={`${styles.toggleOption} ${
                  formData.body_text_alignment === "right"
                    ? styles.activeToggle
                    : ""
                }`}
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    body_text_alignment: "right",
                  }))
                }
                disabled={isLoading}
              >
                Right
              </button>
            </div>
          </div>

          {/* Button Text */}
          <div className={styles.rowInputGroup}>
            <label className={styles.inlineLabel}>Button Text</label>
            <input
              type="text"
              name="button_text"
              value={formData.button_text}
              onChange={handleChange}
              className={styles.textInput}
              placeholder="Enter button text..."
              disabled={isLoading}
            />
          </div>

          {/* Button Link */}
          <div className={styles.rowInputGroup}>
            <label className={styles.inlineLabel}>Button Link</label>
            <input
              type="text"
              name="button_link"
              value={formData.button_link}
              onChange={handleChange}
              className={styles.textInput}
              placeholder="https://example.com"
              disabled={isLoading}
            />
          </div>

          {/* Button Alignment */}
          <div className={styles.rowInputGroup}>
            <label className={styles.inlineLabel}>Button Alignment</label>
            <div className={styles.toggleWrapper}>
              <button
                type="button"
                className={`${styles.toggleOption} ${
                  formData.button_alignment === "left"
                    ? styles.activeToggle
                    : ""
                }`}
                onClick={() =>
                  setFormData((prev) => ({ ...prev, button_alignment: "left" }))
                }
                disabled={isLoading}
              >
                Left
              </button>
              <button
                type="button"
                className={`${styles.toggleOption} ${
                  formData.button_alignment === "center"
                    ? styles.activeToggle
                    : ""
                }`}
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    button_alignment: "center",
                  }))
                }
                disabled={isLoading}
              >
                Center
              </button>
              <button
                type="button"
                className={`${styles.toggleOption} ${
                  formData.button_alignment === "right"
                    ? styles.activeToggle
                    : ""
                }`}
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    button_alignment: "right",
                  }))
                }
                disabled={isLoading}
              >
                Right
              </button>
            </div>
          </div>

          {/* Status Toggle (only for editing) */}
          {editHomeSectionId !== null && (
            <div className={styles.rowInputGroup}>
              <label className={styles.inlineLabel}>Status</label>
              <div className={styles.toggleWrapper}>
                <button
                  type="button"
                  className={`${styles.toggleOption} ${
                    formData.is_active ? styles.activeToggle : ""
                  }`}
                  onClick={() =>
                    setFormData((prev) => ({ ...prev, is_active: true }))
                  }
                  disabled={isLoading}
                >
                  Active
                </button>
                <button
                  type="button"
                  className={`${styles.toggleOption} ${
                    !formData.is_active ? styles.activeToggle : ""
                  }`}
                  onClick={() =>
                    setFormData((prev) => ({ ...prev, is_active: false }))
                  }
                  disabled={isLoading}
                >
                  Inactive
                </button>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            className={`${styles.submitButton} ${
              isLoading ? styles.submitting : ""
            }`}
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span>
                  {editHomeSectionId === null ? "Creating..." : "Updating..."}
                </span>
                <span className={styles.submitIcon}>⟳</span>
              </>
            ) : (
              <>
                <span>Submit Home Section</span>
                <span className={styles.submitIcon}>＋</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateHomeSection;
